import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { ExportButton } from '@/app/ExportButton';
import { formatMoney } from '@/lib/money';
import { BreakdownReport } from './BreakdownReport';
import { ComparisonReport } from './ComparisonReport';
import type { MoneyString } from '@/lib/money';

type CategoryRow = {
  category_id: number | null;
  category_name: string;
  group_name?: string | null;
  total: MoneyString;
  count: number;
  is_managerial?: boolean;
};

type IncomeStatement = {
  period: { from: string; to: string };
  entity_count: number;
  income: { categories: CategoryRow[]; managerial: CategoryRow[]; eliminated: CategoryRow[]; total: MoneyString };
  expense: { categories: CategoryRow[]; managerial: CategoryRow[]; eliminated: CategoryRow[]; total: MoneyString };
  eliminations: {
    total: MoneyString;
    count: number;
    affects_income: MoneyString;
    affects_expense: MoneyString;
    by_purpose: Array<{ purpose: string; total: MoneyString; count: number }>;
    explanation: string;
  };
  result: {
    gross: MoneyString;
    income_after_eliminations: MoneyString;
    expense_after_eliminations: MoneyString;
    net: MoneyString;
    margin_percent: number | null;
  };
};

type CashFlow = {
  period: { from: string; to: string };
  periods: Array<{
    period: string;
    inflow: MoneyString;
    outflow: MoneyString;
    net: MoneyString;
    cumulative: MoneyString;
  }>;
  totals: { inflow: MoneyString; outflow: MoneyString; net: MoneyString };
};

type Meta = {
  title: string;
  generated_at: string;
  generated_by: string;
  disclaimer: string;
};

export function ReportsPage({ selectedIds }: { selectedIds: number[] }) {
  const [tab, setTab] = useState<'dre' | 'fluxo' | 'analise' | 'evolucao'>('dre');
  const { period } = usePeriod();

  // As duas abas recebem o mesmo recorte — é o que torna DRE e fluxo de caixa
  // comparáveis entre si sem ninguém precisar reajustar datas ao trocar de aba.
  const range = { from: period.from, to: period.to };

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-xl font-semibold tracking-tight">Relatórios</h1>

      <div className="mt-4 flex flex-wrap items-end gap-2.5">
        <div className="mb-2 flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
          {([
            ['dre', 'DRE gerencial'],
            ['fluxo', 'Fluxo de caixa'],
            ['analise', 'Análise'],
            ['evolucao', 'Evolução'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                tab === id
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <PeriodBadge />
      </div>

      {/* Regime explicitado: é a distinção que faz os dois relatórios darem
          números diferentes sobre o mesmo dado, e o usuário precisa saber. */}
      {(tab === 'dre' || tab === 'fluxo') && (
      <p className="mt-3 rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
        {tab === 'dre' ? (
          <>
            <strong>Regime de competência:</strong> considera quando o fato aconteceu, mesmo que o
            pagamento seja em outro mês. Responde “quanto ganhei e gastei neste período”.
          </>
        ) : (
          <>
            <strong>Regime de caixa:</strong> considera quando o dinheiro efetivamente entrou ou
            saiu. Responde “quanto entrou e saiu da conta”.
          </>
        )}
      </p>
      )}

      {tab === 'dre' && <IncomeStatementReport range={range} selectedIds={selectedIds} />}
      {tab === 'fluxo' && <CashFlowReport range={range} selectedIds={selectedIds} />}
      {tab === 'analise' && <BreakdownReport range={range} selectedIds={selectedIds} />}
      {tab === 'evolucao' && <ComparisonReport selectedIds={selectedIds} />}
    </div>
  );
}

// -------------------------------------------------------------------

function IncomeStatementReport({
  range,
  selectedIds,
}: {
  range: { from: string; to: string };
  selectedIds: number[];
}) {
  const query = useQuery({
    queryKey: ['report-dre', range, selectedIds],
    queryFn: () =>
      api.get<{ data: IncomeStatement; meta: Meta }>(
        `/reports/income-statement?from=${range.from}&to=${range.to}`,
        { entityIds: selectedIds },
      ),
  });

  if (query.isPending) return <ReportSkeleton />;
  if (query.isError) return <ErrorBox />;

  const d = query.data.data;
  const meta = query.data.meta;
  const hasEliminations = Number(d.eliminations.total) > 0;

  return (
    <div className="mt-5">
      <div className="mb-2 flex justify-end">
        <ExportButton
          path="/reports/income-statement/export"
          query={{ from: range.from, to: range.to }}
          entityIds={selectedIds}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <tbody>
            {/* ---------- Receitas ---------- */}
            <SectionRow label="RECEITAS" total={d.income.total} tone="income" />
            {d.income.categories.map((c) => (
              <CategoryLine key={`i-${c.category_id}`} row={c} />
            ))}
            {d.income.managerial.map((c, i) => (
              <CategoryLine key={`im-${i}`} row={c} badge="transferência" />
            ))}
            {d.income.eliminated.map((c, i) => (
              <CategoryLine key={`ie-${i}`} row={c} badge="eliminada" struck />
            ))}

            {/* ---------- Despesas ---------- */}
            <SectionRow label="DESPESAS" total={d.expense.total} tone="expense" />
            {d.expense.categories.map((c) => (
              <CategoryLine key={`e-${c.category_id}`} row={c} />
            ))}
            {d.expense.managerial.map((c, i) => (
              <CategoryLine key={`em-${i}`} row={c} badge="transferência" />
            ))}
            {d.expense.eliminated.map((c, i) => (
              <CategoryLine key={`ee-${i}`} row={c} badge="eliminada" struck />
            ))}

            {/* ---------- Eliminações ---------- */}
            {hasEliminations && (
              <>
                <tr className="border-t-2 border-slate-200 bg-indigo-50/50 dark:border-slate-700 dark:bg-indigo-950/20">
                  <td className="px-3 py-2 font-medium text-indigo-900 dark:text-indigo-200">
                    (−) Eliminações intragrupo
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-indigo-900 dark:text-indigo-200">
                    {formatMoney(d.eliminations.total)}
                  </td>
                </tr>
                {d.eliminations.by_purpose.map((p) => (
                  <tr key={p.purpose} className="bg-indigo-50/30 dark:bg-indigo-950/10">
                    <td className="py-1 pl-8 pr-3 text-xs text-slate-600 dark:text-slate-400">
                      {p.purpose.replace(/_/g, ' ')}
                      <span className="ml-1.5 text-slate-400">({p.count})</span>
                    </td>
                    <td className="py-1 pr-3 text-right text-xs text-slate-600 dark:text-slate-400">
                      {formatMoney(p.total)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-indigo-50/30 dark:bg-indigo-950/10">
                  <td colSpan={2} className="px-3 pb-2 pt-1 text-xs text-slate-500 dark:text-slate-400">
                    Do total movimentado, <strong>{formatMoney(d.eliminations.affects_income)}</strong>{' '}
                    saíram da receita e{' '}
                    <strong>{formatMoney(d.eliminations.affects_expense)}</strong> da despesa. O
                    restante moveu caixa sem passar pelo resultado.
                  </td>
                </tr>
              </>
            )}

            {/* ---------- Resultado ---------- */}
            <tr className="border-t-2 border-slate-300 dark:border-slate-600">
              <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Receita consolidada</td>
              <td className="px-3 py-2 text-right">{formatMoney(d.result.income_after_eliminations)}</td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Despesa consolidada</td>
              <td className="px-3 py-2 text-right">{formatMoney(d.result.expense_after_eliminations)}</td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40">
              <td className="px-3 py-3 font-semibold">RESULTADO DO PERÍODO</td>
              <td
                className={`px-3 py-3 text-right text-base font-semibold ${
                  Number(d.result.net) >= 0
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-rose-700 dark:text-rose-400'
                }`}
              >
                {formatMoney(d.result.net)}
                {d.result.margin_percent !== null && (
                  <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                    margem {d.result.margin_percent}%
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ReportFooter meta={meta} extra={`${d.entity_count} entidade(s) no recorte`} />
    </div>
  );
}

function SectionRow({
  label,
  total,
  tone,
}: {
  label: string;
  total: MoneyString;
  tone: 'income' | 'expense';
}) {
  return (
    <tr className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40">
      <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </td>
      <td
        className={`px-3 py-2 text-right font-semibold ${
          tone === 'income'
            ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-rose-700 dark:text-rose-400'
        }`}
      >
        {formatMoney(total)}
      </td>
    </tr>
  );
}

function CategoryLine({
  row,
  badge,
  struck,
}: {
  row: CategoryRow;
  badge?: string;
  struck?: boolean;
}) {
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className={`py-1.5 pl-8 pr-3 ${struck ? 'text-slate-400 line-through' : ''}`}>
        {row.group_name && <span className="text-slate-400">{row.group_name} › </span>}
        {row.category_name}
        <span className="ml-1.5 text-xs text-slate-400">({row.count})</span>
        {badge && (
          <span className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
            {badge}
          </span>
        )}
      </td>
      <td className={`py-1.5 pr-3 text-right ${struck ? 'text-slate-400 line-through' : ''}`}>
        {formatMoney(row.total)}
      </td>
    </tr>
  );
}

// -------------------------------------------------------------------

function CashFlowReport({
  range,
  selectedIds,
}: {
  range: { from: string; to: string };
  selectedIds: number[];
}) {
  const query = useQuery({
    queryKey: ['report-cashflow', range, selectedIds],
    queryFn: () =>
      api.get<{ data: CashFlow; meta: Meta }>(
        `/reports/cash-flow?from=${range.from}&to=${range.to}&group_by=month`,
        { entityIds: selectedIds },
      ),
  });

  if (query.isPending) return <ReportSkeleton />;
  if (query.isError) return <ErrorBox />;

  const d = query.data.data;
  const max = Math.max(...d.periods.map((p) => Math.max(Number(p.inflow), Number(p.outflow))), 1);

  return (
    <div className="mt-5">
      <div className="mb-2 flex justify-end">
        <ExportButton
          path="/reports/cash-flow/export"
          query={{ from: range.from, to: range.to, group_by: 'month' }}
          entityIds={selectedIds}
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2.5 font-medium">Período</th>
              <th className="px-3 py-2.5 text-right font-medium">Entradas</th>
              <th className="px-3 py-2.5 text-right font-medium">Saídas</th>
              <th className="px-3 py-2.5 text-right font-medium">Líquido</th>
              <th className="px-3 py-2.5 text-right font-medium">Acumulado</th>
              <th className="w-40 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {d.periods.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-10 text-center text-slate-500 dark:text-slate-400">
                  Nenhum movimento de caixa no período.
                </td>
              </tr>
            ) : (
              d.periods.map((p) => (
                <tr key={p.period} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{p.period}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400">
                    {formatMoney(p.inflow)}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-700 dark:text-rose-400">
                    {formatMoney(p.outflow)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-medium ${
                      Number(p.net) >= 0
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-rose-700 dark:text-rose-400'
                    }`}
                  >
                    {formatMoney(p.net)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-300">
                    {formatMoney(p.cumulative)}
                  </td>
                  <td className="px-3 py-2">
                    {/* Barra dupla: entrada acima, saída abaixo, na mesma escala */}
                    <div className="space-y-0.5">
                      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-emerald-500"
                          style={{ width: `${(Number(p.inflow) / max) * 100}%` }}
                        />
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-rose-500"
                          style={{ width: `${(Number(p.outflow) / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {d.periods.length > 0 && (
            <tfoot className="border-t-2 border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40">
              <tr className="font-semibold">
                <td className="px-3 py-2.5">Total</td>
                <td className="px-3 py-2.5 text-right text-emerald-700 dark:text-emerald-400">
                  {formatMoney(d.totals.inflow)}
                </td>
                <td className="px-3 py-2.5 text-right text-rose-700 dark:text-rose-400">
                  {formatMoney(d.totals.outflow)}
                </td>
                <td
                  className={`px-3 py-2.5 text-right ${
                    Number(d.totals.net) >= 0
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-rose-700 dark:text-rose-400'
                  }`}
                >
                  {formatMoney(d.totals.net)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <ReportFooter
        meta={query.data.meta}
        extra="Une baixas de lançamento e pernas de transferência — as duas fontes de movimento de caixa."
      />
    </div>
  );
}

// -------------------------------------------------------------------

function ReportFooter({ meta, extra }: { meta: Meta; extra?: string }) {
  return (
    <div className="mt-3 space-y-1 text-xs text-slate-400">
      <p>
        {meta.title} · gerado por {meta.generated_by} em{' '}
        {new Date(meta.generated_at).toLocaleString('pt-BR')}
        {extra ? ` · ${extra}` : ''}
      </p>
      <p>{meta.disclaimer}</p>
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div className="mt-5 animate-pulse space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-8 rounded bg-slate-200 dark:bg-slate-800" />
      ))}
    </div>
  );
}

function ErrorBox() {
  return (
    <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
      Não foi possível gerar o relatório.
    </p>
  );
}
